const PREFIX = "attempt_capability";
const memoryTokens = new Map();
const memoryReferences = new Map();

const storageKey = (type, id) => `${PREFIX}:${type}:${id}`;
const perTabToken = (type) => type === "exam_quiz";
const perTabReference = (name) => name === "last_exam_quiz_id";

export function loadAttemptToken(type, id) {
  if (!id) return null;
  const key = storageKey(type, id);
  const memoryToken = memoryTokens.get(key);
  if (memoryToken) return memoryToken;

  if (perTabToken(type)) {
    try {
      const token = window.sessionStorage.getItem(key);
      if (token) return token;

      const legacyToken = window.localStorage.getItem(key);
      if (legacyToken) {
        window.sessionStorage.setItem(key, legacyToken);
        window.localStorage.removeItem(key);
      }
      return legacyToken;
    } catch (_error) {
      return null;
    }
  }

  try {
    return window.localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

export function storeAttemptToken(type, id, token) {
  if (!id || !token) return;
  const key = storageKey(type, id);
  memoryTokens.set(key, token);

  if (perTabToken(type)) {
    try {
      window.sessionStorage.setItem(key, token);
      window.localStorage.removeItem(key);
    } catch (_error) {
      // The in-memory token keeps the current tab usable.
    }
    return;
  }

  try {
    window.localStorage.setItem(key, token);
  } catch (_error) {
    // The in-memory token keeps the current tab usable.
  }
}

export function removeAttemptToken(type, id) {
  if (!id) return;
  const key = storageKey(type, id);
  memoryTokens.delete(key);

  if (perTabToken(type)) {
    try {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
    } catch (_error) {
      // The in-memory token has already been removed.
    }
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch (_error) {
    // The in-memory token has already been removed.
  }
}

export function removeLegacyParticipantToken() {
  try {
    window.localStorage.removeItem("exam_participant_token");
  } catch (_error) {
    // Legacy storage cleanup must never block the exam UI.
  }
}

export function loadAttemptReference(name) {
  const memoryValue = memoryReferences.get(name);
  if (memoryValue) return memoryValue;

  if (perTabReference(name)) {
    try {
      const value = window.sessionStorage.getItem(name);
      if (value) return value;

      const legacyValue = window.localStorage.getItem(name);
      if (legacyValue) {
        window.sessionStorage.setItem(name, legacyValue);
        window.localStorage.removeItem(name);
      }
      return legacyValue;
    } catch (_error) {
      return null;
    }
  }

  try {
    return window.localStorage.getItem(name);
  } catch (_error) {
    return null;
  }
}

export function storeAttemptReference(name, value) {
  if (!name || !value) return;
  memoryReferences.set(name, value);

  if (perTabReference(name)) {
    try {
      window.sessionStorage.setItem(name, value);
      window.localStorage.removeItem(name);
    } catch (_error) {
      // The in-memory reference is sufficient while this tab remains open.
    }
    return;
  }

  try {
    window.localStorage.setItem(name, value);
  } catch (_error) {
    // The in-memory reference is sufficient while this tab remains open.
  }
}

export function removeAttemptReference(name) {
  memoryReferences.delete(name);

  if (perTabReference(name)) {
    try {
      window.sessionStorage.removeItem(name);
      window.localStorage.removeItem(name);
    } catch (_error) {
      // The in-memory reference has already been removed.
    }
    return;
  }

  try {
    window.localStorage.removeItem(name);
  } catch (_error) {
    // The in-memory reference has already been removed.
  }
}

export function attemptHeaders(baseHeaders, type, id) {
  const token = loadAttemptToken(type, id);
  return token
    ? { ...baseHeaders, "X-Participant-Token": token }
    : baseHeaders;
}

export function admissionHeaders(baseHeaders, id) {
  const token = loadAttemptToken("exam_admission", id);
  return token ? { ...baseHeaders, "X-Admission-Token": token } : baseHeaders;
}
